import { EEntitlementType, EJobRecordStatus, EJobRemarkStatus, type IJobRecord } from '@quesmed/types/models';
import { memo, useCallback, useMemo } from 'react';
import { BiSolidDislike, BiSolidLike } from 'react-icons/bi';
import { BsImage } from 'react-icons/bs';
import { FaPercentage } from 'react-icons/fa';
import { FaComments } from 'react-icons/fa6';
import { HiOutlineSparkles } from 'react-icons/hi';
import { IoMdAlert } from 'react-icons/io';
import { MdBarChart } from 'react-icons/md';
import { PiNotebook } from 'react-icons/pi';
import { EFormMode } from '@/config/globals';
import { getJobRecordTitle, getMetricsSummary, recordStatusColorMap } from '@/config/record';
import { CheckBox, Chip, cn, Progress } from '@/core/ui';
import { useRecord } from '@/store/record';
import { formatDateFromUnix } from '@/utils/date';
import { getPercentage } from '@/utils/string';
import { RecordItemActions } from './RecordItemActions';

type Props = {
  item: IJobRecord;
  className?: string;
  isSelected: boolean;
  onValueChange: (value: boolean, recordId: string) => void;
  isAdmin: boolean;
};

function RecordItemComponent({ item, className, isAdmin, isSelected, onValueChange }: Props) {
  const title = getJobRecordTitle(item.data);
  const setCurrentRecord = useRecord.use.setCurrentRecord();
  const setRecordId = useRecord.use.setRecordId();
  const setRecordModal = useRecord.use.setRecordModal();
  const setCount = useRecord((s) => s.stemMap[item.id]);
  const { metricsSummary: summary } = getMetricsSummary(item.question);
  const entitlementName = useMemo(() => {
    const entitlementIds = (item.data as any)?.entitlementIds;
    if (entitlementIds?.length === 1) {
      return EEntitlementType[entitlementIds[0]]
        .split('_')
        .map((id) => id.charAt(0).toUpperCase())
        .join('');
    }
    return null;
  }, [item.data]);

  const bucketName = useMemo(() => {
    const learningPoint = (item.data as any)?.learningPoint ?? '';
    return learningPoint.length < 30 ? learningPoint : null;
  }, [item.data]);

  const handleCheckChange = useCallback(
    (value: boolean) => onValueChange(value, item.id),
    [onValueChange, item.id],
  );

  const onRecordClick = () => {
    setCurrentRecord(item);
    setRecordId(item.id);
    setRecordModal(true, EFormMode.VIEW);
  };

  const resolvedRemarks = item.remarks.filter((r) => r.status === EJobRemarkStatus.RESOLVED).length;
  const totalRemarks = item.remarks.length;

  return (
    <div
      // isPressable
      // disableRipple
      // disableAnimation
      key={item.id}
      // onPress={() => {
      //   onValueChange(!isSelected);
      // }}
      className={cn(
        'flex flex-col justify-between group relative p-3',
        'shrink-0',
        'bg-content1',
        'rounded-lg',
        'border',
        'transition-colors duration-150',
        'shadow-sm',
        className,
        {
          'border-warning-200': isSelected,
          'hover:border-default-400 border-default-200': !isSelected,
        },
      )}
    >
      {/* <div
        className={cn(
          'absolute inset-0 opacity-0 group-hover:opacity-100',
          'transition-opacity duration-300',
        )}
      >
        <div className='absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,0,0,0.05)_1px,transparent_1px)] dark:bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[length:4px_4px]' />
      </div> */}

      <div
        className={cn(
          'flex items-center justify-between space-x-6',
          // 'border-b border-default-200 px-2 -mx-2 pb-2',
        )}
      >
        <div className='flex items-center truncate'>
          <CheckBox color='warning' isSelected={isSelected} onValueChange={handleCheckChange} />

          {setCount && (
            <Chip
              title={`Set ${setCount}`}
              color='secondary'
              classNames={{
                content: 'text-[10px]',
                base: 'h-5 mr-2 px-1 border border-default-200 rounded-md',
              }}
              className={'capitalize'}
              size='sm'
              variant={item.referenceId ? 'bordered' : 'dot'}
            >
              set {setCount}
            </Chip>
          )}

          {/* biome-ignore lint/a11y/useKeyWithClickEvents: <disabled> */}
          <h3
            onClick={onRecordClick}
            title={item.id}
            className='text-sm cursor-pointer hover:underline decoration-emerald-500 items-center truncate gap-2 font-medium text-default-600'
          >
            {item.id}
          </h3>
          {/* <FiExternalLink className='w-6 h-4 text-default-400' /> */}
        </div>

        <RecordItemActions record={item} />
      </div>
      {/* <div className='mt-1'>
        <Progress
          classNames={{
            base: 'max-w-md h-1',
            indicator: 'bg-gradient-to-r from-blue-500 to-green-400',
            label: 'tracking-wider font-medium text-default-600',
            value: 'text-foreground/60 text-xs',
          }}
          radius='sm'
          size='sm'
          value={getPercentage(summary?.correct || 0, summary?.total || 0)}
        />
      </div> */}

      {'stem' in item.data && item.data.stem && (
        <p title={item.data.stem} className='text-xs mt-3 text-content4-foreground line-clamp-1'>
          {item.data.stem}
        </p>
      )}

      <p title={title} className='text-xs mt-2 mb-3 text-content4-foreground line-clamp-1'>
        {title}
      </p>

      <div className='flex items-center gap-1'>
        <Chip
          title={`Record ${item.index}`}
          color='default'
          classNames={{ content: 'text-[10px]', base: 'h-5 px-1 border border-default-200 rounded-md' }}
          className={'capitalize'}
          size='sm'
          variant='solid'
        >
          {item.index}
        </Chip>

        {item.questionId && !isAdmin && (
          <Chip
            title={`Question ${item.questionId}`}
            color={summary?.enabled ? 'success' : 'warning'}
            classNames={{
              content: 'text-[10px]',
              base: cn('h-5 px-1 rounded-md border', {
                'border-success-200': summary?.enabled,
                'border-warning-200': !summary?.enabled,
              }),
            }}
            className={'capitalize'}
            // startContent={recordStatusColorMap[item.status].icon}
            size='sm'
            variant='flat'
          >
            #{item.questionId}
          </Chip>
        )}

        <Chip
          title={`Record Status: ${EJobRecordStatus[item.status]}`}
          classNames={{ content: 'text-[10px]', base: 'h-5 px-1 rounded-md' }}
          className={cn('capitalize', recordStatusColorMap[item.status].className)}
          // startContent={recordStatusColorMap[item.status].icon}
          size='sm'
          variant='flat'
        >
          {EJobRecordStatus[item.status]}
        </Chip>

        {item.remarks?.length > 0 && (
          <div
            title='Remarks'
            className='flex items-center gap-1 border border-default-300 rounded-lg px-1.5 py-0.5'
          >
            <span className='text-[10px] text-content4-foreground'>
              {resolvedRemarks}/{totalRemarks}
            </span>
            <IoMdAlert className='size-3 text-danger-500' />
          </div>
        )}

        {item.assets?.length > 0 && (
          <div
            title='Assets'
            className='flex items-center justify-center gap-1 border border-default-300 rounded-lg px-1.5 py-0.5'
          >
            <span className='text-[10px] text-content4-foreground'>{item.assets.length}</span>
            <BsImage className='size-3 text-success-500' />
          </div>
        )}
        {item.referenceCount > 0 && (
          <div
            title='Referenced Records'
            className='flex items-center justify-center gap-1 border border-default-300 rounded-lg px-1.5 py-0.5'
          >
            <span className='text-[10px] text-content4-foreground'>{item.referenceCount}</span>
            <PiNotebook className='size-3' />
          </div>
        )}

        {entitlementName && (
          <Chip
            title={`Entitlement: ${entitlementName}`}
            color='default'
            classNames={{ content: 'text-[10px]', base: 'h-5 px-1 border border-default-200 rounded-md' }}
            className={'capitalize'}
            size='sm'
            variant='flat'
          >
            {entitlementName}
          </Chip>
        )}

        {bucketName && (
          <Chip
            title={`Bucket: ${bucketName}`}
            color='warning'
            classNames={{ content: 'text-[10px]', base: 'h-5 px-1 border border-default-200 rounded-md' }}
            className={'capitalize'}
            size='sm'
            variant='flat'
          >
            {bucketName}
          </Chip>
        )}

        {(item as any).aiGenerated && isAdmin && (
          <Chip
            title={`AI Generated`}
            color='success'
            className={'capitalize'}
            size='sm'
            radius='sm'
            variant='faded'
          >
            <HiOutlineSparkles className='size-3 text-success-500' />
          </Chip>
        )}
      </div>

      <div className='flex justify-between items-center gap-1 mt-3'>
        <span className='text-xs w-full text-default-400'>
          {formatDateFromUnix(item.createdAt as number, 'DD/MM/YY HH:mm')}
        </span>
        <Chip
          title={`Created By ${item.createdByUser?.firstName}`}
          color='warning'
          classNames={{ content: 'text-[10px]', base: 'h-5 px-1 border border-warning-200 rounded-md' }}
          className={'capitalize'}
          // startContent={recordStatusColorMap[item.status].icon}
          size='sm'
          variant='dot'
        >
          {item.createdByUser?.firstName}
        </Chip>
      </div>

      {isAdmin && item.questionId && (
        <>
          <Progress
            classNames={{
              base: 'h-1 mt-3',
              indicator: 'bg-gradient-to-r from-blue-500 to-green-400',
              // indicator: 'bg-gradient-to-r from-pink-400 to-purple-500',
              label: 'tracking-wider font-medium text-default-600',
              value: 'text-foreground/60 text-xs',
            }}
            radius='sm'
            size='sm'
            value={getPercentage(summary?.correct || 0, summary?.total || 0)}
          />

          <div className='flex font-mono items-center gap-1 justify-between pt-2'>
            <div className='flex items-center gap-1'>
              <Chip
                title={`Question ${item.questionId}`}
                color={summary?.enabled ? 'success' : 'warning'}
                classNames={{
                  content: 'text-[10px]',
                  base: cn('h-5 px-1 rounded-md border', {
                    'border-success-200': summary?.enabled,
                    'border-warning-200': !summary?.enabled,
                  }),
                }}
                className={'capitalize'}
                // startContent={recordStatusColorMap[item.status].icon}
                size='sm'
                variant='flat'
              >
                #{item.questionId}
              </Chip>
              <div
                title='Percent of Correct Attempts'
                className='flex items-center h-5 bg-warning-50 gap-1 border border-default-300 rounded px-1 py-0.5'
              >
                <span className='text-[10px]'>
                  {summary?.total ? ((summary.correct / summary.total) * 100).toFixed(2) : '-'}%
                </span>
                <FaPercentage className='size-3 text-warning-500' />
              </div>
            </div>

            <div className='flex items-center gap-1'>
              <div
                title='Total Attempts'
                className='flex items-center h-5 gap-1 border border-default-300 rounded px-1 py-0.5'
              >
                <span className='text-[10px]'>{summary?.total || 0}</span>
                <MdBarChart className='size-3 text-success-500' />
              </div>
              <div
                title='Comments'
                className='flex items-center h-5 gap-1 border border-default-300 rounded px-1 py-0.5'
              >
                <span className='text-[10px]'>{summary?.commentCount || 0}</span>
                <FaComments className='size-3 text-cyan-500' />
              </div>
              <div
                title='Likes and Dislikes'
                className='flex items-center h-5 gap-1 border border-default-300 rounded px-1 py-0.5'
              >
                <div className='flex items-center gap-1'>
                  <BiSolidLike className='size-3 text-success-600' />
                  <span className='text-[10px]'>{summary?.likes || 0}</span>
                </div>
                <div className='flex items-center gap-1'>
                  <BiSolidDislike className='size-3 text-danger-600' />
                  <span className='text-[10px]'>{summary?.dislikes || 0}</span>
                </div>
              </div>
              {/* <Chip
              title='Total Attempts'
              color='default'
              classNames={{ content: 'text-[10px]', base: 'h-5 px-1 rounded-md' }}
              size='sm'
              variant='flat'
            >
              {summary?.total ? formatNumber(summary.total) : '-'}
            </Chip>
            <Chip
              title='Correct Attempts'
              color='success'
              classNames={{ content: 'text-[10px]', base: 'h-5 px-1 rounded-md' }}
              size='sm'
              variant='flat'
            >
              {summary?.correct ? formatNumber(summary.correct) : '-'}
            </Chip>
            <Chip
              title='Incorrect Attempts'
              color='danger'
              classNames={{ content: 'text-[10px]', base: 'h-5 px-1 rounded-md' }}
              size='sm'
              variant='flat'
            >
              {summary?.incorrect ? formatNumber(summary.incorrect) : '-'}
            </Chip> */}
            </div>
          </div>
        </>
      )}

      {/* <div className='mt-auto border-t border-default-200'>
        <Button
          size='sm'
          variant='light'
          endContent={<FaArrowRight className='size-3.5' />}
          className='w-full'
        >
          View Details
        </Button>
      </div> */}
    </div>
  );
}

export const RecordItem = memo(RecordItemComponent);
RecordItemComponent.displayName = 'RecordItem';
